
import { defineComponent, reactive, ref, watch } from 'vue';
import Dialog from 'primevue/dialog';
import InputText from 'primevue/inputtext';
import Button from 'primevue/button';
import Checkbox from 'primevue/checkbox';
import FileManager from '@/components/file-manager/FileManager.vue';
import { UserState } from '@/composables/User';
import { NestApiType, nestCall } from '@/composables/nestApi';
import {
  isDemoSettingsVisible,
  demoOverrides,
  demoCampaigns,
  saveDemoOverrides,
  saveDemoCampaigns,
  hideDemoSettings,
  DemoOverrides,
} from '@/composables/DemoMode';

interface CampaignItem {
  id: number;
  name: string;
}

export default defineComponent({
  name: 'DemoSettingsDialog',

  components: {
    Dialog,
    InputText,
    Button,
    Checkbox,
    FileManager,
  },

  setup() {
    const form = reactive<DemoOverrides>({
      name: '',
      url: '',
      logo_url: '',
      push_icon_url: '',
    });

    const showFileManager = ref(false);
    const fileManagerTarget = ref<'logo_url' | 'push_icon_url'>('logo_url');

    // Campaign lists
    const loadingCampaigns = ref(false);
    const automationCampaigns = ref<CampaignItem[]>([]);
    const bulkCampaigns = ref<CampaignItem[]>([]);
    const selectedAutomationIds = ref<number[]>([]);
    const selectedBulkIds = ref<number[]>([]);

    const templates = ref<CampaignItem[]>([]);
    const selectedTemplateIds = ref<number[]>([]);

    const fetchCampaigns = async () => {
      loadingCampaigns.value = true;
      const idShop = UserState.activeShop?.id;
      if (!idShop) {
        loadingCampaigns.value = false;
        return;
      }

      try {
        const [autoResult, bulkResult, tplResult] = await Promise.all([
          nestCall('get', NestApiType.CLIENT_API, '/campaigns/automations', {}, '', undefined, true, { limit: 200 }),
          nestCall('get', NestApiType.CLIENT_API, '/campaigns/bulk', {}, '', undefined, true, { limit: 200 }),
          nestCall('get', NestApiType.CLIENT_API, '/templates', {}, '', undefined, true, {
            limit: 200,
            'filters[0][field]': 'deleted',
            'filters[0][operator]': 'EQUALS',
            'filters[0][values]': 'false',
            'filters[1][field]': 'created_from_campaign',
            'filters[1][operator]': 'IS_NULL',
            'filters[1][values]': '',
            'filters[2][field]': 'id_campaign',
            'filters[2][operator]': 'IS_NULL',
            'filters[2][values]': '',
            'filters[3][field]': 'config',
            'filters[3][operator]': 'JSON_NOT_EXISTS',
            'filters[3][values]': 'origin_template_id',
          }),
        ]);

        if (autoResult?.data) {
          automationCampaigns.value = autoResult.data.map((c: any) => ({
            id: c.id,
            name: c.name || c.settings?.name || `Campagne #${c.id}`,
          }));
        }

        if (bulkResult?.data) {
          bulkCampaigns.value = bulkResult.data.map((c: any) => ({
            id: c.id,
            name: c.name || c.settings?.name || `Campagne #${c.id}`,
          }));
        }

        if (tplResult?.data) {
          templates.value = tplResult.data.map((t: any) => ({
            id: t.id,
            name: t.name || `Modèle #${t.id}`,
          }));
        }
      } catch {
        // Silently fail
      }

      loadingCampaigns.value = false;
    };

    const toggleCampaign = (type: 'automation' | 'bulk' | 'template', id: number) => {
      const list = type === 'automation' ? selectedAutomationIds : type === 'bulk' ? selectedBulkIds : selectedTemplateIds;
      const idx = list.value.indexOf(id);
      if (idx === -1) {
        list.value.push(id);
      } else {
        list.value.splice(idx, 1);
      }
    };

    // Sync form with current state when dialog opens
    watch(isDemoSettingsVisible, (visible) => {
      if (visible) {
        Object.assign(form, demoOverrides);
        selectedAutomationIds.value = [...demoCampaigns.automationIds];
        selectedBulkIds.value = [...demoCampaigns.bulkIds];
        selectedTemplateIds.value = [...demoCampaigns.templateIds];
        fetchCampaigns();
      }
    });

    const openFileManager = (target: 'logo_url' | 'push_icon_url') => {
      fileManagerTarget.value = target;
      showFileManager.value = true;
    };

    const onChooseFile = (file: any) => {
      if (file && file.url) {
        form[fileManagerTarget.value] = file.url;
      }
      showFileManager.value = false;
    };

    const onSave = () => {
      saveDemoOverrides({ ...form });
      saveDemoCampaigns({
        automationIds: [...selectedAutomationIds.value],
        bulkIds: [...selectedBulkIds.value],
        templateIds: [...selectedTemplateIds.value],
      });
      hideDemoSettings();
    };

    const onClose = () => {
      hideDemoSettings();
    };

    const onImgError = (e: Event) => {
      (e.target as HTMLImageElement).style.display = 'none';
    };

    const onImgLoad = (e: Event) => {
      (e.target as HTMLImageElement).style.display = 'block';
    };

    return {
      isDemoSettingsVisible,
      form,
      showFileManager,
      openFileManager,
      onChooseFile,
      onSave,
      onClose,
      onImgError,
      onImgLoad,
      loadingCampaigns,
      automationCampaigns,
      bulkCampaigns,
      selectedAutomationIds,
      selectedBulkIds,
      templates,
      selectedTemplateIds,
      toggleCampaign,
    };
  },
});
